/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base typography styles for the Soft UI Dashboard PRO React.
 * You can add new typography style using this file.
 * You can customized the typography styles for the entire Soft UI Dashboard PRO React using thie file.
 */

// Soft UI Dashboard PRO React Base Styles
import { letterSpacing, textTransform } from '@mui/system'
import colors from 'assets/theme/base/colors'

// Soft UI Dashboard PRO React Helper Functions
import pxToRem from 'assets/theme/functions/pxToRem'

const { dark } = colors

const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  xl3: 1920,
}

const baseProperties = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontFamilyAlt: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
}

const baseHeadingProperties = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightMedium,
}

const baseDisplayProperties = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightLight,
  lineHeight: 1.2,
}

const typography = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontSize: pxToRem(48),
    lineHeight: 1.25,
    ...baseHeadingProperties,
  },

  h2: {
    fontSize: pxToRem(36),
    lineHeight: 1.3,
    ...baseHeadingProperties,
  },

  h3: {
    fontSize: pxToRem(30),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h4: {
    fontSize: pxToRem(24),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    color: '#262627',
  },

  h5: {
    fontSize: pxToRem(20),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    color: '#262627',
  },

  h6: {
    fontSize: pxToRem(16),
    lineHeight: 1.625,
    ...baseHeadingProperties,
  },

  subtitle1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.625,
  },

  subtitle2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 1.6,
  },

  body1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeLG,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: '150%',
  },

  // body2: {
  //   fontFamily: baseProperties.fontFamily,
  //   fontSize: baseProperties.fontSizeMD,
  //   fontWeight: baseProperties.fontWeightRegular,
  //   lineHeight: '150%'
  // },

  // NEW VARIANTS

  body1Medium: {
    fontFamily: baseProperties.fontFamily,

    fontSize: baseProperties.fontSizeSM,

    [`@media (min-width:${breakpoints.sm}px)`]: {
      fontSize: baseProperties.fontSizeSM,
    },

    [`@media (min-width:${breakpoints.md}px)`]: {
      fontSize: baseProperties.fontSizeMD,
    },

    [`@media (min-width:${breakpoints.lg}px)`]: {
      fontSize: baseProperties.fontSizeLG,
    },

    fontWeight: 600,
    lineHeight: '150%',
    color: '#262627',
  },

  body2Fun: {
    fontFamily: baseProperties.fontFamilyAlt,

    fontSize: baseProperties.fontSizeMD,

    [`@media (min-width:${breakpoints.sm}px)`]: {
      fontSize: baseProperties.fontSizeMD,
    },

    [`@media (min-width:${breakpoints.md}px)`]: {
      fontSize: baseProperties.fontSizeMD,
    },

    [`@media (min-width:${breakpoints.lg}px)`]: {
      fontSize: baseProperties.fontSizeLG,
    },
    fontWeight: 600,
    lineHeight: '100%',
    // letterSpacing: 2,
  },

  body2FunHeader: {
    fontFamily: baseProperties.fontFamilyAlt,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: 600,
    lineHeight: '100%',
    letterSpacing: 0.32,
    color: '#495057',
  },

  headerTitle: {
    fontFamily: baseProperties.fontFamily,

    fontSize: pxToRem(26),

    [`@media (min-width:${breakpoints.md}px)`]: {
      fontSize: pxToRem(28),
    },

    [`@media (min-width:${breakpoints.lg}px)`]: {
      fontSize: pxToRem(32),
    },
    fontWeight: 800,
    lineHeight: '117%',
  },
  body3Weird: {
    fontFamily: baseProperties.fontFamilyAlt,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: 600,
    lineHeight: '100%',
    // letterSpacing: 2,
  },

  body3Medium: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 500,
    fontSize: baseProperties.fontSizeSM,
    lineHeight: '100%',
    letterSpacing: '0%',
  },

  body3Light: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: 400,
    lineHeight: '100%',
  },

  body3: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: 700,
    lineHeight: '150%',
    color: '#39558C',
  },

  body2: {
    fontFamily: baseProperties.fontFamily,

    fontSize: baseProperties.fontSizeMD,

    [`@media (min-width:${breakpoints.sm}px)`]: {
      fontSize: baseProperties.fontSizeMD,
    },

    [`@media (min-width:${breakpoints.md}px)`]: {
      fontSize: baseProperties.fontSizeMD,
    },

    [`@media (min-width:${breakpoints.xl}px)`]: {
      fontSize: baseProperties.fontSizeLG,
    },
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: '150%',
    color: '#262627',
  },

  body2Medium: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: 500,
    lineHeight: '150%',
    color: '#262627',
  },

  statusLabel: {
    fontFamily: baseProperties.fontFamily,

    fontSize: baseProperties.fontSizeLG,

    [`@media (min-width:${breakpoints.sm}px)`]: {
      fontSize: baseProperties.fontSizeLG,
    },

    [`@media (min-width:${breakpoints.md}px)`]: {
      fontSize: baseProperties.fontSizeXL,
    },

    [`@media (min-width:${breakpoints.lg}px)`]: {
      fontSize: baseProperties.fontSizeXL,
    },

    fontWeight: 600,
    lineHeight: '150%',
  },

  // END NEW VARIANTS

  inputNote: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,

    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.6,
    color: '#ccc',
    marginLeft: '12px',
  },
  valueTitle: {
    fontFamily: baseProperties.fontFamilyAlt,
    fontSize: pxToRem(14),
    fontWeight: 600,
    letterSpacing: '0.28px',
    color: '#A3A7AA',
    lineHeight: 1.6,
  },
  valuePoint: {
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(14),
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 'normal',
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightBold,
    lineHeight: 1.5,
    textTransform: 'uppercase',
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.25,
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },
}

export default typography
